import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Shield, 
  Heart, 
  Users, 
  Compass, 
  Home,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

const VETERAN_IMAGE = "https://images.unsplash.com/photo-1606885460422-f52fec94d0a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxtaWxpdGFyeSUyMHZldGVyYW4lMjBzbWlsaW5nJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzczMjQ5NTc0fDA&ixlib=rb-4.1.0&q=85";

const services = [
  {
    icon: Compass,
    title: "Behavioral Health Resource Navigation",
    description: "Guiding veterans through the complex landscape of behavioral health services to find the right support for their needs."
  },
  {
    icon: Heart,
    title: "Mental Health Service Access",
    description: "Helping veterans understand and access community mental health services and support programs."
  },
  {
    icon: Users,
    title: "Family Support",
    description: "Providing guidance and support to families of veterans as they navigate behavioral health challenges together."
  },
  {
    icon: Shield,
    title: "Program Information",
    description: "Sharing information about veteran behavioral health programs and resources available in the community."
  },
  {
    icon: Home,
    title: "Community Connections",
    description: "Connecting veterans with veteran support organizations and community resources for ongoing assistance."
  },
];

const VeteranSupport = () => {
  return (
    <div className="min-h-screen" data-testid="veteran-support-page">
      {/* Hero Section */}
      <section className="bg-brand-blue text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-white/10 rounded-full px-4 py-2 mb-6">
                <Shield size={18} />
                <span className="text-sm font-medium">Dedicated Veteran Support</span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-6 tracking-tight" data-testid="page-title">
                Veteran Behavioral Health Navigation
              </h1>
              <p className="text-lg text-white/90 leading-relaxed">
                Behavioral Health Navigation & Workforce Agency is committed to supporting veterans and their families as they navigate behavioral health resources and community support systems.
              </p>
            </div>
            <div className="hidden md:block">
              <img 
                src={VETERAN_IMAGE} 
                alt="Veteran smiling" 
                className="rounded-2xl shadow-2xl object-cover w-full h-[400px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="font-heading text-3xl font-bold text-brand-blue mb-6">
              Understanding Veteran Needs
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Many veterans face unique challenges during and after their transition from military service, including difficulties navigating healthcare systems, accessing mental health services, and reconnecting with community support networks. Our Veteran Support initiative provides guidance and resource navigation designed to help veterans access appropriate behavioral health services and support programs.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-brand-neutral">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-brand-blue mb-4">
              Services for Veterans
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our veteran support services are designed to address the unique needs of those who have served our country.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card 
                  key={index} 
                  className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-all border border-gray-100 hover:border-brand-teal"
                  data-testid={`service-card-${index}`}
                >
                  <CardContent className="p-0">
                    <div className="w-14 h-14 bg-brand-blue/10 rounded-xl flex items-center justify-center mb-6">
                      <Icon size={28} className="text-brand-blue" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-brand-blue mb-3">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="bg-gradient-to-br from-brand-blue to-brand-teal p-8 rounded-2xl text-white">
              <Shield size={48} className="mb-6" />
              <h3 className="font-heading text-2xl font-bold mb-4">
                Our Commitment to Veterans
              </h3>
              <p className="text-white/90 mb-6 leading-relaxed">
                By providing navigation assistance and resource guidance, the agency works to ensure that veterans and their families are connected to the support systems they need. We honor their service by helping them access the care they deserve.
              </p>
              <ul className="space-y-3">
                {[
                  "Respectful, veteran-centered approach",
                  "Understanding of military culture",
                  "Connection to veteran-specific resources",
                  "Ongoing support and follow-up"
                ].map((item, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <CheckCircle size={18} className="flex-shrink-0" />
                    <span className="text-white/90">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-6">
              <h2 className="font-heading text-3xl font-bold text-brand-blue">
                Ready to Connect?
              </h2>
              <p className="text-gray-600 leading-relaxed">
                If you're a veteran or family member seeking behavioral health navigation support, we're here to help. Our team understands the unique challenges veterans face and is committed to providing compassionate, effective guidance.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/request-support">
                  <Button 
                    className="bg-brand-orange hover:bg-brand-orange/90 text-white rounded-full px-6 py-3"
                    data-testid="request-support-btn"
                  >
                    Request Support
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button 
                    variant="outline" 
                    className="border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white rounded-full px-6 py-3"
                    data-testid="contact-btn"
                  >
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VeteranSupport;
